<!-- Warm-up scheme — how one lift ramps. Two doors, one editor.

     DEFAULT (from Kit, `?for=default`): the lift's saved scheme and its saved
     last jump. Every session starts from these.
     TODAY (from Lifts and the Ramp): starts from the default, and every edit is
     for this session only — kept beside the ramp's position in session storage
     by lib/jump.js, which also offers it back as the new default. The screen is
     the same in both, because two editors for one scheme drift apart.

     A PUSHED DETAIL SCREEN: the bar alone (the lift's name), a back button, no
     large title. See src/pages/SHELL.md.

     THE PREVIEW IS THE POINT. A percentage is an abstraction until it is a
     weight on a sleeve, so every edit re-runs the ramp through `rampFor` from
     lib/jump.js — the SAME function the Ramp screen calls, never a second
     implementation — and each step says what the bar will actually carry.
     Two answers to "what goes on the bar" is the one defect this app cannot have.

     THE PREVIEW IS ALSO HONEST. `buildRamp` drops a warm-up that lands on or
     under the one before it, or on or past the last warm-up, and refuses a top
     set the kit cannot make. Both show up here, as words, before anybody is
     standing under the bar.

     NO FLOATING RAIL. "Add a warm-up" sits in the flow under the last row: a
     sticky rail over a list of fields covered the row being typed into. -->
<template>
  <IonPage class="work-up scheme-page">
    <AppHeader :title="lift ? lift.name : 'Warm-ups'" :back="backTo" :back-text="isDefault ? 'Kit' : 'Lifts'" />

    <IonContent :fullscreen="true">
      <!-- A record that does not exist is a real state, not a crash: a deep
           link can outlive the thing it points at. -->
      <div v-if="!lift" class="empty">
        <p class="serif empty-title">Not found</p>
        <p class="note">That lift is no longer here.</p>
        <button type="button" class="wu-btn" @click="goHome">Lifts</button>
      </div>

      <div v-else-if="draft" class="wrap">
        <!-- The bar says which lift; this says what it is working up to, and
             which scheme is being edited. -->
        <header class="intro">
          <p class="serif lede">{{ lede }}</p>
          <p class="mode">{{ modeLine }}</p>
          <p v-if="problem" class="mode">{{ problem }}</p>
        </header>

        <!-- THE WEIGHTS FIRST. What goes on the bar is what this screen is for;
             the percentages behind it are a setting, and they fold away under
             "Edit warm-ups" until somebody wants them. The default door (from
             Kit) opens with them out, because editing is why it was opened. -->
        <div class="rampbar">
          <h2 class="wu-label rampbar-label">{{ rampTitle }}</h2>
          <!-- The setup as words, to send a training partner or keep. -->
          <button type="button" class="share" aria-label="Share these warm-ups" @click="shareSetup">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12M8 7l4-4 4 4" /><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" /></svg>
          </button>
          <button
            type="button"
            class="edit"
            :aria-expanded="editing ? 'true' : 'false'"
            aria-controls="warmup-editor"
            @click="toggleEdit"
          >
            {{ editing ? 'Done' : 'Edit warm-ups' }}
            <svg class="chev" :class="{ open: editing }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </button>
        </div>

        <!-- TODAY: keep it, or go back. Outside the fold, so a changed day says
             so even with the settings put away. Shown and hidden, never mounted:
             see the note on .notes below. -->
        <div v-show="!isDefault && differs" class="today">
          <button type="button" class="wu-btn-quiet" @click="backToDefault">Back to my default</button>
          <button type="button" class="wu-btn" @click="saveAsDefault">Save as my default</button>
        </div>

        <!-- The settings, folded. v-show rather than v-if: the fields keep
             their state and nothing around them is re-keyed while typing. -->
        <div id="warmup-editor" v-show="editing" class="editor">
          <!-- ---- start ---- -->
          <h2 class="wu-label label label--first">Start</h2>
          <div class="start">
            <span class="key">
              <span class="k">{{ startIsBar ? 'Empty bar' : 'First warm-up' }}</span>
              <!-- seed.js starts a deadlift at plate height on purpose: pulling
                 from a lower bar is a different exercise, not a lighter one.
                 Say so, rather than quietly resetting what the lifter chose. -->
              <span v-if="startSub" class="sub">{{ startSub }}</span>
            </span>
            <span class="entry">
              <input
                class="wu-field-sm wt"
                type="number"
                inputmode="decimal"
                min="0"
                step="0.5"
                aria-label="First warm-up, weight"
                :value="draft.startWeight"
                @input="setNum(draft, 'startWeight', $event, 0, 2000)"
                @blur="resync($event, draft.startWeight)"
              >
              <span class="u" aria-hidden="true">{{ unit }} ×</span>
              <!-- Two fields, not one "6–10": a phone's numeric keypad has no dash. -->
              <input
                class="wu-field-sm rep"
                type="number"
                inputmode="numeric"
                min="1"
                max="50"
                step="1"
                aria-label="First warm-up, reps"
                :value="draft.startReps"
                @input="setNum(draft, 'startReps', $event, 1, 50)"
                @blur="endRange(draft, 'startReps', 'startRepsTo'); resync($event, draft.startReps)"
              >
              <span class="u" aria-hidden="true">–</span>
              <input
                class="wu-field-sm rep opt"
                type="number"
                inputmode="numeric"
                min="1"
                max="50"
                step="1"
                placeholder=" "
                aria-label="First warm-up, up to reps, optional"
                :value="draft.startRepsTo ?? ''"
                @input="setUpTo(draft, 'startRepsTo', $event)"
                @blur="endRange(draft, 'startReps', 'startRepsTo'); resync($event, draft.startRepsTo)"
              >
            </span>
          </div>

          <!-- ---- the warm-ups ---- -->
          <h2 class="wu-label label">Warm-ups · % of top set or last warm-up → what goes on</h2>

          <!-- EVERY CHILD OF .wrap STAYS PUT WHILE SOMEBODY TYPES. Ionic renders
             its slots outside Vue's optimised path, so this list is diffed by
             key, and a note appearing beside the steps MOVED the whole list —
             which blurs the field being typed into and drops the keyboard
             after one digit. What comes and goes lives inside a wrapper that
             is always there, or is shown and hidden rather than mounted. -->
          <div class="notes">
            <p v-if="!draft.steps.length" class="note empty-line">No warm-ups. Straight to the top set, or add one.</p>
            <p v-else-if="dropped" class="note">A warm-up that lands on a neighbour is dropped.</p>
          </div>

          <ol v-if="draft.steps.length" class="steps">
            <li v-for="(s, i) in draft.steps" :key="s.key" class="step">
              <span class="ord" aria-hidden="true">{{ i + 1 }}</span>

              <span class="pctline">
                <input
                  class="wu-field-sm pct"
                  type="number"
                  inputmode="decimal"
                  min="1"
                  max="99"
                  step="0.5"
                  :aria-label="`Warm-up ${i + 1}, percent`"
                  :value="s.percent"
                  @input="setNum(s, 'percent', $event, 1, 99)"
                  @blur="resync($event, s.percent)"
                >
                <span class="u" aria-hidden="true">%</span>
                <span class="u times" aria-hidden="true">×</span>
                <input
                  class="wu-field-sm rep"
                  type="number"
                  inputmode="numeric"
                  min="1"
                  max="50"
                  step="1"
                  :aria-label="`Warm-up ${i + 1}, reps`"
                  :value="s.reps"
                  @input="setNum(s, 'reps', $event, 1, 50)"
                  @blur="endRange(s, 'reps', 'repsTo'); resync($event, s.reps)"
                >
                <span class="u" aria-hidden="true">–</span>
                <input
                  class="wu-field-sm rep opt"
                  type="number"
                  inputmode="numeric"
                  min="1"
                  max="50"
                  step="1"
                  placeholder=" "
                  :aria-label="`Warm-up ${i + 1}, up to reps, optional`"
                  :value="s.repsTo ?? ''"
                  @input="setUpTo(s, 'repsTo', $event)"
                  @blur="endRange(s, 'reps', 'repsTo'); resync($event, s.repsTo)"
                >
              </span>

              <span class="res">
                <template v-if="topSet && ramp.ok">
                  <template v-if="setFor(i)">
                    <span class="serif w">{{ fmt(setFor(i).total) }}</span><span class="u">{{ unit }}</span>
                  </template>
                  <!-- Dropped is said in words, not colour. -->
                  <span v-else class="u">Dropped</span>
                </template>
                <span v-else class="u faint" aria-hidden="true">—</span>
              </span>

              <span class="wu-seg seg-sm" role="group" :aria-label="`Warm-up ${i + 1}, a share of`">
                <button type="button" :aria-pressed="s.of !== 'last'" @click="setOf(s, 'top')">of top</button>
                <button type="button" :aria-pressed="s.of === 'last'" @click="setOf(s, 'last')">of last</button>
              </span>

              <span class="tools">
                <button type="button" class="tool" :disabled="i === 0" :aria-label="`Move warm-up ${i + 1} earlier`" @click="move(i, -1)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 15l6-6 6 6" /></svg>
                </button>
                <button type="button" class="tool" :disabled="i === draft.steps.length - 1" :aria-label="`Move warm-up ${i + 1} later`" @click="move(i, 1)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                <button type="button" class="tool" :aria-label="`Remove warm-up ${i + 1}`" @click="dropStep(i)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17" /></svg>
                </button>
              </span>
            </li>
          </ol>

          <!-- The last warm-up is not a step: it is the top set minus the jump, one
             rep, and every "of last" step above hangs off it. -->
          <div v-if="draft.steps.length" class="lastwarm">
            <span class="key"><span class="k">Last warm-up</span></span>
            <span class="res">
              <template v-if="lastWarm">
                <span class="serif w">{{ fmt(lastWarm.total) }}</span><span class="u">{{ unit }} × 1</span>
              </template>
              <span v-else class="u faint" aria-hidden="true">—</span>
            </span>
            <span class="jumpline">
              <span class="stepper" role="group" :aria-label="isDefault ? 'Last warm-up jump' : 'Last warm-up jump, today only'">
                <button
                  type="button" class="nudge" :disabled="jump <= rule.step"
                  :aria-label="`${fmt(rule.step)} ${unit} less`" @click="nudge(-1)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M6 12h12" /></svg>
                </button>
                <span class="jump-value" aria-live="polite">{{ fmt(jump) }} <span class="u">{{ unit }}</span></span>
                <button
                  type="button" class="nudge" :disabled="jump >= rule.max"
                  :aria-label="`${fmt(rule.step)} ${unit} more`" @click="nudge(1)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M12 6v12M6 12h12" /></svg>
                </button>
              </span>
              <span class="u">under the top set</span>
            </span>
          </div>

          <div class="add">
            <button type="button" class="wu-btn-quiet wu-btn--row" :disabled="!ready || !draft" @click="addStep">Add a warm-up</button>
          </div>
        </div>

        <!-- ---- the ramp it makes, on the bar ---- -->
        <section class="preview">
          <ol v-if="topSet && kit && ramp.ok" class="ramp">
            <li v-for="(set, i) in ramp.sets" :key="i" class="rset">
              <BarSleeve
                class="rbar"
                compact
                :stack="set.stack"
                :plates="kitPlates"
                :unit="unit"
                :collar-weight="Number(kit.collarWeight) || 0"
              />
              <div class="rtext">
                <p class="setline">
                  <span class="serif w">{{ fmt(set.total) }}</span><span class="u">{{ unit }}</span>
                  <span class="u reps">{{ repsText(set.reps, set.repsTo) }}</span>
                </p>
                <p class="plates">{{ set.stack.length ? `${set.stack.map(fmt).join(' · ')} each side` : 'Empty bar' }}</p>
                <p v-if="set.isTop" class="serif tag">top set</p>
                <p v-else-if="set.isLastWarmUp" class="serif tag">last warm-up</p>
              </div>
            </li>
          </ol>
        </section>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonPage, IonContent, onIonViewWillEnter, onIonViewWillLeave } from '@ionic/vue';
import { AppHeader, removeWithUndo } from '../core/ui';
import BarSleeve from '../components/BarSleeve.vue';
import { debounce, haptic, toast } from '../core/util.js';
import { shareUrl } from '../core/native.js';
import { warmupsText } from '../lib/warmups.js';
import {
  liftsReady, findLift, rampStepsReady, kitsReady, findKit, platesReady,
} from '../store.js';
import { currentKit, platesFor, savedScheme, saveScheme } from '../lib/seed.js';
import { bare, fmt, repsText, rangeTo, jumpRule, clampJump, savedJump } from '../lib/loading.js';
import {
  rampFor, schemeFor, jumpFor, todayScheme, todayJump, setTodayScheme, setTodayJump, clearToday, saveTodayAsDefault,
} from '../lib/jump.js';

const props = defineProps({ id: { type: [String, Number], default: null } });
const route = useRoute();
const router = useRouter();
// Resolved from the ROUTER, never imported from router.js: that module imports
// product-routes.js, which imports this page, so a top-level `import { HOME }`
// closes a cycle and the binding is still uninitialised when this module runs.
// The whole app booted to a blank screen with no error.
const HOME = router.resolve({ name: 'lifts' }).path;

// The id from the page's own props, not the global route: Ionic keeps this page
// alive under the next one, and the route it would read by then is that one's.
const lift = computed(() => findLift(props.id ?? route.params.id));

// Which door this is. Read on entry, because Ionic reuses a page for the same
// path whatever its query says.
const isDefault = ref(route.query.for === 'default');

/** Back to where the lifter came from: Kit for the default, the ramp for today. */
const backTo = computed(() => {
  if (!lift.value) return HOME;
  return isDefault.value ? router.resolve({ name: 'kit' }).path : `/tabs/lifts/${lift.value.id}/ramp`;
});
const goHome = () => router.replace(HOME);

const kit = computed(() => (lift.value?.kit ? findKit(lift.value.kit) : null) || currentKit());
const kitPlates = computed(() => (kit.value ? platesFor(kit.value) : []));
const unit = computed(() => kit.value?.unit || 'kg');
// The bar alone: collars go on with the first plate.
const emptyBar = computed(() => (kit.value ? bare(kit.value) / 100 : 0));
const topSet = computed(() => Number(lift.value?.topSet) || 0);
const rule = computed(() => jumpRule(unit.value));

// THE DRAFT is what the fields edit, in both modes: a plain copy of the scheme
// and the jump, written through on every change. Steps carry a local `key` so a
// reorder moves the row rather than its contents.
const draft = ref(null);
const jump = ref(0);
let keySeq = 0;

// The settings fold. Out on the default door, away on today's: from Lifts the
// question is "what am I lifting", and the percentages are one tap away.
const editing = ref(false);
function toggleEdit() {
  flushNow();
  editing.value = !editing.value;
  haptic('tick');
}

// NOTHING MAY ALLOCATE AN ID BEFORE THE STORE HAS HYDRATED — ids come from one
// shared sequence and a row created too early collides with a stored one. The
// draft, and with it every control that writes, waits for every list.
const ready = ref(false);
Promise.all([liftsReady, rampStepsReady, kitsReady, platesReady])
  .catch(() => {})
  .then(() => { ready.value = true; load(); });

function load() {
  flushNow();
  isDefault.value = route.query.for === 'default';
  editing.value = isDefault.value;
  const l = lift.value;
  if (!ready.value || !l) { draft.value = null; return; }
  const s = isDefault.value ? savedScheme(l) : schemeFor(l);
  draft.value = { ...s, steps: s.steps.map((x) => ({ ...x, key: (keySeq += 1) })) };
  jump.value = isDefault.value ? savedJump(l, unit.value) : jumpFor(l, unit.value);
}
onIonViewWillEnter(load);
onIonViewWillLeave(() => flushNow());

// A write per keystroke is a write per keystroke, so the default is saved on a
// short debounce. Today's is session storage and is written at once, which is
// what lets the preview below read it the way the Ramp does.
const dirty = new Map();
const flushSaves = debounce(() => {
  for (const save of dirty.values()) save();
  dirty.clear();
}, 250);
function flushNow() {
  for (const save of dirty.values()) save();
  dirty.clear();
}
onBeforeUnmount(flushNow);

function changed() {
  const l = lift.value;
  const d = draft.value;
  if (!l || !d) return;
  if (!isDefault.value) { setTodayScheme(l, d); return; }
  const u = unit.value;
  dirty.set('scheme', () => {
    const j = jump.value;
    saveScheme(l, d, j === savedJump(findLift(l.id), u) ? {} : { lastJump: j });
  });
  flushSaves();
}

// Today differs from the default in its warm-ups, its jump, or both.
const differs = computed(() => !!lift.value
  && (todayScheme(lift.value) != null || todayJump(lift.value, unit.value) != null));

function backToDefault() {
  clearToday(lift.value);
  load();
  haptic('pop');
}

function saveAsDefault() {
  saveTodayAsDefault(lift.value, unit.value);
  load();
  haptic('pop');
  toast('Saved as your default.');
}

// The live preview. Today: exactly what the Ramp will load, read the one way
// every screen reads it. Default: the saved scheme as it is being edited, which
// reaches the store a debounce later.
const ramp = computed(() => {
  if (!lift.value || !topSet.value || !kit.value || !draft.value) return { ok: false, top: null };
  return isDefault.value
    ? rampFor(lift.value, kit.value, kitPlates.value, { scheme: draft.value, jump: jump.value })
    : rampFor(lift.value, kit.value, kitPlates.value);
});

/** The set a step became, or null when it was dropped. */
const setFor = (i) => (ramp.value.ok ? ramp.value.sets.find((s) => s.step === i) ?? null : null);

// Steps plus the last warm-up, which every scheme with steps gets. The start is
// the bar, and nobody counts the bar.
const count = computed(() => (draft.value?.steps.length ? draft.value.steps.length + 1 : 0));
const lastWarm = computed(() => (ramp.value.ok ? ramp.value.sets.find((s) => s.isLastWarmUp) ?? null : null));
const dropped = computed(() => ramp.value.ok && draft.value?.steps.some((_, i) => !setFor(i)));

/**
 * Share the setup on screen as text: the share sheet on a phone, the browser's
 * where it has one, the clipboard otherwise. What is shared is what is shown —
 * today's warm-ups on today's door, the default on Kit's.
 */
async function shareSetup() {
  if (!lift.value || !draft.value) return;
  flushNow();
  const title = `${lift.value.name} warm-ups`;
  const text = warmupsText({
    name: lift.value.name,
    unit: unit.value,
    kind: lift.value.kind,
    scheme: draft.value,
    jump: jump.value,
    emptyBar: emptyBar.value,
    topSet: ramp.value.ok ? topSet.value : 0,
    sets: ramp.value.ok ? ramp.value.sets : [],
  });
  if (await shareUrl(undefined, title, text)) return;
  if (navigator.share) {
    try { await navigator.share({ title, text }); return; } catch (e) { if (e?.name === 'AbortError') return; }
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Warm-ups copied.');
  } catch {
    toast("Couldn't share that.");
  }
}

const rampTitle = computed(() => {
  if (!ramp.value.ok || !topSet.value) return 'The ramp';
  const n = ramp.value.sets.length;
  return `${n} ${n === 1 ? 'set' : 'sets'} to ${fmt(topSet.value)} ${unit.value}`;
});

// ---- the words at the top ----
const REP_WORD = { 1: 'single', 2: 'double', 3: 'triple' };
const lede = computed(() => {
  if (!topSet.value) return 'No top set yet';
  const n = Number(lift.value?.topSetReps) || 1;
  return `Warm-ups to a ${fmt(topSet.value)} ${unit.value} ${REP_WORD[n] ?? `set of ${n}`}`;
});

const modeLine = computed(() => {
  const n = count.value;
  const c = n ? `${n} ${n === 1 ? 'warm-up' : 'warm-ups'}` : 'no warm-ups';
  if (isDefault.value) return `Your default, for every session · ${c}`;
  return differs.value ? `Today only · ${c} · default unchanged` : `Today · ${c} · same as your default`;
});

/** Why the weights on the right are not there, in words. */
const problem = computed(() => {
  if (!topSet.value) return 'Enter a top set on Lifts and the weights fill in.';
  if (!kit.value) return 'No kit. Set the bar and plates in Kit first.';
  if (ramp.value.ok) return '';
  const t = ramp.value.top;
  const near = [t?.below, t?.above].filter((x) => x != null).map((x) => fmt(x));
  return `${fmt(topSet.value)} ${unit.value} can't be loaded with this kit.`
    + (near.length ? ` Nearest: ${near.join(' or ')} ${unit.value}.` : '');
});

const startIsBar = computed(() => Number(draft.value?.startWeight) === emptyBar.value);
const startSub = computed(() => {
  if (lift.value?.kind === 'deadlift') return `plate height: bar and a pair of ${unit.value === 'lb' ? '45s' : '20s'}`;
  return startIsBar.value ? '' : `empty bar ${fmt(emptyBar.value)} ${unit.value}`;
});

/** Mid-typing gives '' and '-' — leave the model alone rather than storing NaN. */
function setNum(row, field, event, min, max) {
  const v = parseFloat(event.target.value);
  if (!Number.isFinite(v)) return;
  row[field] = Math.min(max, Math.max(min, v));
  changed();
}

/** The top of a range is optional: emptied, there is no range. */
function setUpTo(row, field, event) {
  const raw = event.target.value;
  if (raw === '') row[field] = null;
  else {
    const v = parseInt(raw, 10);
    if (!Number.isFinite(v)) return;
    row[field] = Math.min(50, Math.max(1, v));
  }
  changed();
}

/** A range that stopped being one (5 to 3, 5 to 5) is put away, not shown and ignored. */
function endRange(row, from, to) {
  if (row[to] == null || rangeTo(row[from], row[to])) return;
  row[to] = null;
  changed();
}

/** Put the model's value back in the field once the person has left it. */
function resync(event, value) {
  event.target.value = value ?? '';
  flushNow();
}

function setOf(row, of) {
  if ((row.of === 'last' ? 'last' : 'top') === of) return;
  row.of = of;
  changed();
  haptic('selection');
}

function nudge(by) {
  const next = clampJump(jump.value + by * rule.value.step, unit.value);
  if (next === jump.value) return;
  jump.value = next;
  if (isDefault.value) changed();
  else setTodayJump(lift.value, next, unit.value);
  haptic('selection');
}

function move(i, by) {
  const list = draft.value.steps;
  const j = i + by;
  if (j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
  changed();
  haptic('selection');
}

function addStep() {
  if (!ready.value || !draft.value) return;
  const list = draft.value.steps;
  const last = list[list.length - 1];
  // Halfway from the last step to all of what it is a share of, which is where
  // the next one usually wants to be. A first step is the default's: half the top set.
  list.push({
    key: (keySeq += 1),
    percent: last ? Math.min(95, Math.round((Number(last.percent) + 100) / 2)) : 50,
    of: last?.of ?? 'top',
    reps: last ? Math.max(1, Number(last.reps) - 1) : 5,
    repsTo: null,
  });
  changed();
  haptic('pop');
}

/** Remove, reversibly: the step leaves at once, and Undo puts it back where it was. */
function dropStep(i) {
  const d = draft.value;
  const [row] = d.steps.splice(i, 1);
  changed();
  haptic('pop');
  removeWithUndo({
    text: `${fmt(Number(row.percent) || 0)}% removed.`,
    revert: () => {
      if (draft.value !== d) return; // the screen has moved on to another scheme
      d.steps.splice(Math.min(i, d.steps.length), 0, row);
      changed();
    },
  });
}
</script>

<style scoped>
/* Values are screens.py `scheme()`'s. */
.wrap { padding: 8px var(--wu-pad) 24px; }

.empty { padding: 48px var(--wu-pad) 0; text-align: center; }
.empty-title { margin: 0 0 8px; font-size: 28px; line-height: 1.1; }
.empty .wu-btn { margin-top: 20px; min-width: 10rem; }

/* ---- the words at the top ---- */
.intro { display: flex; flex-direction: column; gap: 6px; }
.lede { margin: 0; font-size: 18px; font-style: italic; line-height: 1.2; color: var(--dim); }
.mode { margin: 0; font: 400 0.75rem/1.5 var(--face-mono); color: var(--dim); }

.label { margin: 22px 0 8px; }
.label--first { margin-top: 26px; }
.label--ramp { margin-top: 32px; }

.note { margin: 0 0 10px; font: 400 0.75rem/1.5 var(--face-mono); color: var(--dim); }
.empty-line { padding: 16px 0 0; border-top: 1px solid var(--line); }

/* The unit and the operators beside a field. */
.u { font: 400 0.75rem/1 var(--face-mono); color: var(--dim); white-space: nowrap; }
.faint { color: var(--faint); }

/* Fields: `.wu-field-sm` draws them; these set the widths. */
input.wt { width: 64px; min-width: 64px; }
input.pct { width: 60px; min-width: 60px; padding: 0 6px; } /* room for 92.5 */
input.rep { width: 44px; min-width: 44px; padding: 0 4px; }
/* An empty "up to" is optional, and says so with its outline, not a colour. */
input.opt:placeholder-shown:not(:focus) { border-style: dashed; }

/* ---- start ---- */
.start {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 60px;
  padding: 8px 0;
  box-sizing: border-box;
  border-top: 1px solid var(--line);
}
.key { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.k { font: 400 0.875rem/1.2 var(--face-mono); color: var(--ink); }
.sub { font: 400 0.6875rem/1.3 var(--face-mono); color: var(--dim); }
.entry { flex: none; display: flex; align-items: center; gap: 6px; }

/* ---- a warm-up ----
 * Line one is the design's row: the share, the reps, what goes on the bar.
 * Line two says what the share is OF, and holds the move and remove tools.
 * All of it on one line needs ~440px; a phone has 342. */
.steps { list-style: none; margin: 0; padding: 0; }
.step {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  grid-template-areas:
    'ord pct res'
    '. seg tools';
  align-items: center;
  column-gap: 10px;
  row-gap: 6px;
  padding: 8px 0 6px;
  border-top: 1px solid var(--line);
}
.ord { grid-area: ord; font: 400 0.75rem/1 var(--face-mono); color: var(--faint); }
.pctline { grid-area: pct; display: flex; align-items: center; gap: 6px; min-width: 0; }
.pctline .times { margin-left: 4px; }
.step .res { grid-area: res; }
.step .seg-sm { grid-area: seg; justify-self: start; }
.tools { grid-area: tools; display: flex; justify-self: end; margin-right: -14px; }

.res { justify-self: end; display: flex; align-items: baseline; gap: 4px; }
.w { font-size: 26px; line-height: 1; font-variant-numeric: tabular-nums; }

/* The compact of top | of last: the shared pill, a size down. */
.seg-sm { flex: none; }
.seg-sm > button { padding: 0 9px; font-size: 0.75rem; }

.tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  color: var(--dim);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tool:active { background: var(--seg); }
.tool:disabled { color: var(--line); cursor: default; }

/* ---- the last warm-up ---- */
.lastwarm {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  row-gap: 8px;
  padding: 12px 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.jumpline { grid-column: 1 / -1; display: flex; align-items: center; gap: 12px; }
.stepper { display: flex; align-items: center; gap: 10px; }
.nudge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: 1px solid var(--field-line);
  border-radius: 12px;
  background: var(--field);
  color: var(--ink);
  cursor: pointer;
}
.nudge:active { background: var(--seg); }
/* Stated in shape, not colour. */
.nudge:disabled { border-style: dashed; border-color: var(--faint); background: transparent; color: var(--faint); cursor: default; }
.jump-value {
  min-width: 56px;
  text-align: center;
  font: 500 0.9375rem/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
}
.jump-value .u { font-size: 0.9375rem; }

/* ---- the fold ---- */
.rampbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 22px;
}
.rampbar-label { margin: 0 auto 0 0; }
.share {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 -4px 0 0;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  color: var(--ink);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.share:active { background: var(--seg); }
.edit {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--field-line);
  border-radius: 22px;
  background: transparent;
  color: var(--ink);
  font: 400 0.8125rem/1 var(--face-mono);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.edit:active { background: var(--seg); }
.chev { transition: transform var(--dur, 140ms) var(--ease, ease); }
.chev.open { transform: rotate(180deg); }
@media (prefers-reduced-motion: reduce) { .chev { transition: none; } }
.editor { padding-bottom: 8px; }

/* ---- add, and today's two answers ---- */
.add { display: flex; padding-top: 16px; }
.today { display: flex; flex-direction: column; gap: 10px; padding-top: 14px; }

/* ---- the ramp on the bar: the main part of the screen ----
 * One row per set: a small picture of the bar on the left, running in from the
 * screen edge like every bar in the app, and the weight to read on the right.
 * Not the Ramp's big drawing — here the list is for reading the whole climb at
 * a glance, five or six sets on one screen. */
.preview { margin-top: 10px; }
.ramp { --bleed: var(--wu-pad); --sleeve-span: 40vw; list-style: none; margin: 0; padding: 0; }
/* The picture's column is sized here, not by the picture: BarSleeve takes its
   width from its container, so an `auto` column gave it none and it vanished. */
.rset {
  display: grid;
  grid-template-columns: 36vw minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid var(--line);
}
.rtext { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.setline { display: flex; align-items: baseline; gap: 4px; margin: 0; }
.plates { margin: 0; font: 400 0.75rem/1.4 var(--face-mono); color: var(--dim); font-variant-numeric: tabular-nums; }
.setline .reps { margin-left: 8px; }
.tag { margin: 0; font-size: 15px; font-style: italic; color: var(--dim); }
.rbar { flex: none; }
</style>
